import { browserTransferOptions, clientSDK } from "@istari-frontend/api-client";
import { Job, JobStatusName, OptionalTotalPageJob, OS, Source } from "@istari/istari-client";
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { DEFAULT_CACHE_TIME, QUERY_STALE_TIME_MS_30S } from "~/constants";
import { getLatestJobStatusName } from "~/jobs/model/job-status";
import { registerGeneratedUpload, runUploadPayload } from "~/resource/data-source/upload-payloads";
import {
  awaitUploadSessionReady,
  captureUploadSession,
  isUploadSessionCurrent,
  type UploadSession,
  uploadSessionCallbacks,
} from "~/resource/data-source/upload-session-callbacks";

let completionAudioContext: AudioContext | null = null;
export const playCompletionSound = () => {
  if (typeof window === "undefined") return;
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    completionAudioContext = completionAudioContext ?? new Ctor();
    const ctx = completionAudioContext;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    const t = ctx.currentTime;
    oscillator.frequency.setValueAtTime(1800, t);
    oscillator.frequency.exponentialRampToValueAtTime(700, t + 0.05);
    oscillator.frequency.setValueAtTime(1800, t + 0.12);
    oscillator.frequency.exponentialRampToValueAtTime(700, t + 0.165);
    gain.gain.setValueAtTime(0.0001, t);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.exponentialRampToValueAtTime(0.28, t + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    gain.gain.setValueAtTime(0.0001, t + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.28, t + 0.122);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.165);
    oscillator.start(t);
    oscillator.stop(t + 0.2);
  } catch {}
};

export const playClickSound = () => {
  if (typeof window === "undefined") return;
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    completionAudioContext = completionAudioContext ?? new Ctor();
    const ctx = completionAudioContext;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    const t = ctx.currentTime;
    osc.frequency.setValueAtTime(2200, t);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.02);
    gain.gain.setValueAtTime(0.0001, t);
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.exponentialRampToValueAtTime(0.28, t + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
    osc.start(t);
    osc.stop(t + 0.035);
  } catch {}
};

export const playRustleSound = () => {
  if (typeof window === "undefined") return;
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    completionAudioContext = completionAudioContext ?? new Ctor();
    const ctx = completionAudioContext;
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    filter.type = "lowpass";
    const t = ctx.currentTime;
    filter.frequency.setValueAtTime(5200, t);
    filter.frequency.linearRampToValueAtTime(6800, t + 0.05);
    filter.Q.setValueAtTime(0.35, t);
    gain.gain.setValueAtTime(0.0001, t);

    const bufferSize = 0.2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.linearRampToValueAtTime(0.03, t + 0.035);
    gain.gain.linearRampToValueAtTime(0.0001, t + 0.25);
    noise.start(t);
    noise.stop(t + 0.26);
  } catch {}
};

export const showCompletionNotification = (opts?: { title?: string; body?: string }) => {
  if (typeof window === "undefined") return;
  if (!("Notification" in window)) return;
  try {
    const title = opts?.title || "Job completed";
    const body = opts?.body;
    if (Notification.permission === "granted") {
      if (body) {
        new Notification(title, { body });
      } else {
        new Notification(title);
      }
      return;
    }
    if (Notification.permission === "default") {
      void Notification.requestPermission().then((perm) => {
        if (perm === "granted") {
          if (body) {
            new Notification(title, { body });
          } else {
            new Notification(title);
          }
        }
      });
    }
  } catch {}
};

export const formatShortId = (id: string | null | undefined) => (id && id.length > 8 ? `${id.slice(0, 8)}…` : id || "");

export const getJobResourceLabel = (job: Job) => (job?.modelId ? `model ${formatShortId(job.modelId)}` : "resource");

export const getJobFunctionName = (job: Job) => job._function?.name ?? "";

export type JobsFilter = {
  modelId?: string | null;
  statusName?: JobStatusName | null;
  allUsers?: boolean;
  page?: number;
  size?: number;
  state?: "active" | "archived" | null;
  sort?: string | null;
  name?: string | null;
  /**
   * `false` skips the server-side count: `total` and `pages` come back null, and the registry
   * serves the page without resolving every job the caller can see. Listings that only read
   * `items` should pass `false`.
   */
  includeTotal?: boolean;
};

export const useJobs = (filter: JobsFilter, options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const { modelId, statusName, allUsers, page, size, state, sort, name } = filter;
  const includeTotal = filter.includeTotal ?? true;
  return useQuery<OptionalTotalPageJob>({
    queryKey: [
      "jobs",
      {
        modelId: modelId ?? null,
        statusName: statusName ?? null,
        allUsers: !!allUsers,
        page,
        size,
        state: state ?? "active",
        sort: sort ?? "-created",
        name: name ?? null,
        includeTotal,
      },
    ],
    queryFn: () =>
      clientSDK.listJobs(
        modelId ?? null,
        statusName ?? null,
        !!allUsers,
        null,
        name ?? null,
        page,
        size,
        state ?? "active",
        sort ?? "-created",
        includeTotal,
      ),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    select: (pageData) => {
      const optimistic = (queryClient.getQueryData<Job[]>(["jobs:optimistic"]) ?? []) as Job[];
      const serverItems = (pageData.items ?? []) as Job[];

      // Completion toast/sound and optimistic-row cleanup now live in the live job-events consumer
      // (useJobStatusEvents), so they fire once off the event stream rather than off every poll.
      // The display merge below already hides an optimistic row once its server counterpart arrives.

      const serverHasModelId = new Set<string>(serverItems.map((j) => j?.modelId ?? ""));
      const matchesFilter = (job: Job) => {
        if (modelId && job.modelId !== modelId) return false;
        if (statusName && deriveLatestStatus(job) !== statusName) return false;
        return true;
      };
      const extraOptimistic = optimistic.filter(
        (job) => !serverHasModelId.has(job?.modelId ?? "") && matchesFilter(job),
      );
      const mergedItems = [...extraOptimistic, ...serverItems] as Job[];
      return Object.assign(Object.create(Object.getPrototypeOf(pageData)), pageData, {
        items: mergedItems,
      }) as OptionalTotalPageJob;
    },
    staleTime: QUERY_STALE_TIME_MS_30S,
  });
};

export const deriveLatestStatus = (job: Job): JobStatusName => {
  return getLatestJobStatusName(job.statusHistory) ?? JobStatusName.Unknown;
};

export const toParametersFile = (parameters?: object | File) => {
  if (parameters instanceof File) return parameters;
  if (parameters && typeof parameters === "object") {
    return new File([JSON.stringify(parameters)], "parameters.json");
  }
  return new File(["sample"], "sample.txt");
};

export type HandleAuth = (ctx: { functionName: string; authInfo: unknown }) => Promise<Source | null>;

export type RunJobInput = {
  modelId?: string | null;
  functionName: string;
  toolKey: string;
  toolVersion: string;
  operatingSystem: string;
  assignedAgentId?: string;
  agentId?: string;
  parameters?: object | File;
  primarySource: Source;
  extraSources?: Source[];
  authInfo?: unknown;
  handleAuth?: HandleAuth;
};

/**
 * The total for an uncounted `useJobs` listing, fetched as one counted single-row request.
 *
 * Counting resolves every job the caller can see, so it is refreshed on the cache interval
 * instead of on every poll of the page it labels.
 */
export const jobsCountQueryOptions = (filter: Pick<JobsFilter, "allUsers" | "state" | "name">) => {
  const { allUsers, state, name } = filter;
  return queryOptions({
    // Outside the ["jobs"] prefix, like ["jobs:optimistic"]: the run-job optimistic update rewrites
    // every ["jobs"] entry's items, and cancel/complete invalidate the prefix. Neither should touch
    // the cached count or restart its clock.
    queryKey: ["jobs:count", { allUsers: !!allUsers, state: state ?? "active", name: name ?? null }],
    queryFn: () =>
      clientSDK.listJobs(null, null, !!allUsers, null, name ?? null, 1, 1, state ?? "active", "-created", true),
    select: (page) => page.total ?? 0,
    staleTime: DEFAULT_CACHE_TIME,
    // staleTime alone never refetches a mounted query; the interval is what refreshes the total
    // for a panel that stays open, at one counted request per interval instead of per poll.
    refetchInterval: DEFAULT_CACHE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useJobsCount = (
  filter: Pick<JobsFilter, "allUsers" | "state" | "name">,
  options?: { enabled?: boolean },
) => useQuery({ ...jobsCountQueryOptions(filter), enabled: options?.enabled ?? true });

export const useRunJob = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: Omit<RunJobInput, "parameters"> & { operationId: string; session: UploadSession }) =>
      runUploadPayload(input.operationId, async (parametersFile, context) => {
        await awaitUploadSessionReady(input.session);
        if (!input.modelId) {
          throw new Error("Model ID is required");
        }
        const baseSources = [input.primarySource, ...(input.extraSources ?? [])];
        let secretSource: Source | null = null;
        if (input.authInfo && input.handleAuth) {
          secretSource = await input.handleAuth({ functionName: input.functionName, authInfo: input.authInfo });
        }
        await awaitUploadSessionReady(input.session);
        const sourcesInfo = secretSource ? [...baseSources, secretSource] : baseSources;
        // convert the string to the corresponding enum key
        const mappedOSKey = (Object.keys(OS) as (keyof typeof OS)[]).find((key) => OS[key] === input.operatingSystem);
        const mappedOS = mappedOSKey ? OS[mappedOSKey] : undefined;
        return clientSDK.addJobLazy(input.modelId, parametersFile, {
          functionName: input.functionName,
          toolName: input.toolKey,
          toolVersion: input.toolVersion,
          operatingSystem: mappedOS,
          assignedAgentId: input.assignedAgentId,
          sources: sourcesInfo,
          signal: context.signal,
          salt: context.salt,
          onProgress: context.onProgress,
          transfer: browserTransferOptions,
        });
      }),
    retry: false,
    onMutate: async (input) => {
      if (!input.modelId || !isUploadSessionCurrent(input.session)) return {};
      await queryClient.cancelQueries({ queryKey: ["jobs"] });
      if (!isUploadSessionCurrent(input.session)) return {};
      const previousEntries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ["jobs"] })
        .map((q) => ({ key: q.queryKey, data: queryClient.getQueryData<OptionalTotalPageJob>(q.queryKey) }));
      type JobStatusItem = NonNullable<Job["statusHistory"]>[number];
      const runningStatus: JobStatusItem = { name: JobStatusName.Running, created: new Date() } as JobStatusItem;
      const tempJob = {
        id: `temp-${input.modelId}`,
        modelId: input.modelId,
        created: new Date(),
        statusHistory: [runningStatus],
      } as Job;
      const optimisticList = (queryClient.getQueryData<Job[]>(["jobs:optimistic"]) ?? []) as Job[];
      const hasOptimisticForModel = optimisticList.some((j) => j?.modelId === input.modelId);
      const nextOptimistic = hasOptimisticForModel
        ? optimisticList.map((j) => (j?.modelId === input.modelId ? tempJob : j))
        : [tempJob, ...optimisticList];
      queryClient.setQueryData<Job[]>(["jobs:optimistic"], nextOptimistic);
      previousEntries.forEach(({ key, data }) => {
        if (!data) return;
        const items: Job[] = data.items ?? [];
        const exists = items.some((job) => job?.modelId === input.modelId);
        let nextItems: Job[];
        if (exists) {
          nextItems = items.map((job) => {
            if (job?.modelId !== input.modelId) return job;
            const currentHistory = (job.statusHistory ?? []) as JobStatusItem[];
            const updatedJob = Object.assign(Object.create(Object.getPrototypeOf(job)), job, {
              statusHistory: [runningStatus, ...currentHistory],
            }) as Job;
            return updatedJob;
          });
        } else {
          nextItems = [tempJob, ...items];
        }
        const nextPage = Object.assign(Object.create(Object.getPrototypeOf(data)), data, {
          items: nextItems,
        }) as OptionalTotalPageJob;
        queryClient.setQueryData<OptionalTotalPageJob>(key, nextPage);
      });
      return { previousEntries };
    },
    onError: (_err, input, context) => {
      if (!isUploadSessionCurrent(input.session)) return;
      toast.error("An error occurred while starting a job.");
      type CacheSnapshot = { key: readonly unknown[]; data: OptionalTotalPageJob | undefined };
      const ctx = context as { previousEntries?: CacheSnapshot[] } | undefined;
      ctx?.previousEntries?.forEach(({ key, data }) => {
        queryClient.setQueryData(key, data);
      });
      if (input?.modelId) {
        const optimisticList = (queryClient.getQueryData<Job[]>(["jobs:optimistic"]) ?? []) as Job[];
        const nextOptimistic = optimisticList.filter((j) => j?.modelId !== input.modelId);
        queryClient.setQueryData<Job[]>(["jobs:optimistic"], nextOptimistic);
      }
    },
    onSettled: () => {
      // Do not invalidate because it takes time for jobs to start, and the optimistic update will be overwritten when we fail to find the job in the list due to the delay.
    },
    meta: {
      triggerGlobalInvalidation: false,
    },
  });
  const toOperation = ({ parameters, ...metadata }: RunJobInput) => ({
    ...metadata,
    primarySource: copySource(metadata.primarySource),
    extraSources: metadata.extraSources?.map(copySource),
    authInfo: metadata.authInfo === undefined ? undefined : structuredClone(metadata.authInfo),
    session: captureUploadSession(),
    operationId: registerGeneratedUpload(toParametersFile(parameters)),
  });
  return {
    ...mutation,
    mutate: (input: RunJobInput, callbacks?: Parameters<typeof mutation.mutate>[1]) => {
      const operation = toOperation(input);
      mutation.mutate(operation, uploadSessionCallbacks(callbacks, operation.session));
    },
    mutateAsync: (input: RunJobInput, callbacks?: Parameters<typeof mutation.mutateAsync>[1]) => {
      const operation = toOperation(input);
      return mutation.mutateAsync(operation, uploadSessionCallbacks(callbacks, operation.session));
    },
  };
};

function copySource(source: Source) {
  return new Source(
    source.revisionId,
    source.fileId,
    source.resourceType,
    source.resourceId,
    source.relationshipIdentifier,
  );
}

export const useJob = (jobId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => clientSDK.getJob(jobId!),
    enabled: (options?.enabled ?? true) && !!jobId,
  });
};
